import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { ROLE_LABELS } from "@/context/AuthContext";
import { fmtDate } from "@/components/ui-kit";
import { StaffPhoto } from "@/components/staff/StaffPhoto";
import { toast } from "sonner";
import { Cake, Award, Send, Check, Loader2 } from "lucide-react";

function when(d) { return d === 0 ? "Today" : d === 1 ? "Tomorrow" : `in ${d} days`; }

function Item({ x, onSent }) {
  const [busy, setBusy] = useState(false);
  const bday = x.kind === "birthday";
  async function send() {
    setBusy(true);
    try { await api.post(`/staff-mgmt/staff/${x.user_id}/greet`, { kind: x.kind }); toast.success(`Greeting sent to ${x.name}`); onSent(x); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  return (
    <div data-testid={`celebration-${x.kind}-${x.user_id}`} className={`flex items-center gap-3 py-2.5 border-t border-slate-100 first:border-0 ${x.in_days === 0 ? "bg-pink-50/60 -mx-3 px-3 rounded-xl" : ""}`}>
      <div className="relative"><StaffPhoto fileId={x.photo_file_id} name={x.name} size="h-11 w-11 text-sm" />
        <span className={`absolute -bottom-1 -right-1 h-5 w-5 rounded-full flex items-center justify-center ring-2 ring-white text-white ${bday ? "bg-pink-500" : "bg-amber-500"}`}>{bday ? <Cake size={11} /> : <Award size={11} />}</span></div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm text-slate-900 truncate">{x.name}</div>
        <div className="text-xs text-slate-500 truncate">{bday ? "Birthday" : `${x.years} year${x.years > 1 ? "s" : ""} at school`} • {when(x.in_days)} ({fmtDate(x.date)}) • {x.designation || ROLE_LABELS[x.role]}</div>
      </div>
      {x.greeted ? <span data-testid={`greeted-${x.kind}-${x.user_id}`} className="text-xs text-emerald-700 flex items-center gap-1"><Check size={14} /> Sent</span> : (
        <button data-testid={`greet-${x.kind}-${x.user_id}`} onClick={send} disabled={busy}
          className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full text-white transition-transform active:scale-95 disabled:opacity-60 ${bday ? "bg-pink-500 hover:bg-pink-600" : "bg-amber-500 hover:bg-amber-600"}`}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} {bday ? "Wish" : "Congratulate"}
        </button>
      )}
    </div>
  );
}

export function CelebrationsCard() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/staff-mgmt/celebrations", { params: { days: 30 } }).then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  if (!items) return null;
  const onSent = (x) => setItems((l) => l.map((y) => (y.user_id === x.user_id && y.kind === x.kind ? { ...y, greeted: true } : y)));
  return (
    <div data-testid="celebrations-card" className="bg-white rounded-2xl shadow-sm p-5">
      <div className="flex items-center gap-2 mb-2">
        <Cake size={18} className="text-pink-500" /><h3 className="font-display font-bold text-slate-900 flex-1">Birthdays & work anniversaries</h3>
        <span className="text-xs text-slate-400">next 30 days</span>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-slate-500">No upcoming celebrations. Add dates of birth and joining dates in <Link to="/staff" className="text-violet-700 underline">Staff profiles</Link> to see them here.</div>
      ) : items.slice(0, 8).map((x) => <Item key={`${x.kind}-${x.user_id}`} x={x} onSent={onSent} />)}
    </div>
  );
}
